'use client'

import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react'
import { useCompanyApi, useDebounce } from '@repo/sdk'
import { cn } from '@repo/ui/lib'
import { useQuery } from '@tanstack/react-query'
import { ChevronDownIcon } from 'lucide-react'
import { useState } from 'react'

interface Props {
  className?: string
  onChange: (...event: any[]) => void
  value?: number
}

export const CompanyInput = ({ className, onChange, value }: Props) => {
  const [query, setQuery] = useState('')
  const { getCompaniesList, getCompany } = useCompanyApi()

  const setDebouncedQuery = useDebounce((value: string) => {
    setQuery(value)
  }, 300)

  const { data, isPending } = useQuery({
    queryFn: async () => getCompaniesList(query),
    queryKey: ['companiesSearch', query],
  })

  const { data: company } = useQuery({
    queryFn: async () => {
      if (!value) throw new Error('No company selected')
      return getCompany(value)
    },
    queryKey: ['company', value],
  })

  const handleOnChange = (value: 'no results' | { id: number; title: string }) => {
    if (value === 'no results') {
      return
    }
    onChange(value.id)
  }

  return (
    <Combobox
      as={'div'}
      immediate
      // @ts-ignore
      onChange={handleOnChange}
      onClose={() => setQuery('')}
      value={company?.data}
    >
      <div className={'relative'}>
        <ComboboxInput
          autoComplete={'off'}
          className={cn(
            'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          // @ts-ignore
          displayValue={company => company?.title}
          onChange={e => setDebouncedQuery(e.target.value)}
          placeholder="Company"
        />
        <ComboboxButton className="group absolute inset-y-0 right-0 px-2.5">
          <ChevronDownIcon className="size-4 fill-black/60 group-data-[hover]:fill-black" />
        </ComboboxButton>
      </div>
      <ComboboxOptions
        anchor={'bottom start'}
        className={'z-[1500] min-w-28 cursor-pointer border bg-white empty:invisible'}
      >
        {data?.data?.map(company => (
          <ComboboxOption
            className="pointer-events-auto p-2 data-[focus]:bg-blue-100"
            key={company.id}
            value={company}
          >
            <div>
              <p className={'font-semibold'}>{company.title}</p>
              <div className={'flex gap-1 text-sm'}>
                {[
                  company?.country?.code && company.country.code,
                  company?.address?.city && company.address.city,
                  company?.account?.vat && company.account.vat,
                ]
                  .filter(Boolean)
                  .map((item, index, array) => (
                    <p key={index}>
                      {item}
                      {index < array.length - 1 && ','}
                    </p>
                  ))}
              </div>
            </div>
          </ComboboxOption>
        ))}
        {!isPending && query?.length > 2 && data?.data.length === 0 && (
          <ComboboxOption className={'p-2'} value="no-results">
            No results found
          </ComboboxOption>
        )}
      </ComboboxOptions>
    </Combobox>
  )
}
